import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { setActiveTenant } from '@peruselab/api-client/apiClient';
import {
  listMyInvitations,
  listMyTenants,
  respondToInvitation,
  type Tenant,
  type TenantInvitation
} from '@peruselab/api-client/tenants';
import { ActiveTenantContext, type ActiveTenantValue } from './activeTenant';
import { rememberTenant, rememberedTenant, resolveActiveTenant, tenantScopeKey } from './activeTenantStore';

/**
 * #1051 -- keeps the API client acting for the tenant the user chose (P6). The choice is remembered
 * per user on this device; an organisation they no longer belong to falls back to the personal
 * tenant. When the remembered tenant is an organisation, the app waits for the organisations list
 * before it renders, so nothing is first fetched from the wrong tenant; otherwise it starts at once.
 */
export function ActiveTenantProvider({ userId, children }: { readonly userId: string; readonly children: ReactNode }) {
  const { t } = useTranslation();
  const [remembered] = useState(() => rememberedTenant(userId));
  const [tenants, setTenants] = useState<readonly Tenant[] | null>(null);
  const [invitations, setInvitations] = useState<readonly TenantInvitation[]>([]);
  const [activeId, setActiveId] = useState<string | null>(remembered);

  const load = useCallback(async () => {
    const [mine, invited] = await Promise.all([
      listMyTenants().catch(() => [] as readonly Tenant[]),
      listMyInvitations().catch(() => [] as readonly TenantInvitation[])
    ]);
    setTenants(mine);
    setInvitations(invited);
    return mine;
  }, []);

  useEffect(() => {
    let current = true;
    void load().then((mine) => {
      if (!current) return;
      setActiveId((id) => resolveActiveTenant(mine, id)?.id ?? null);
    });
    return () => {
      current = false;
    };
  }, [load]);

  const active = useMemo(() => (tenants ? resolveActiveTenant(tenants, activeId) : null), [tenants, activeId]);
  // The API client follows the resolved tenant -- set before children render and fetch.
  const activeTenantId = tenants ? (active?.id ?? null) : remembered;
  setActiveTenant(activeTenantId);
  useEffect(() => () => setActiveTenant(null), []);

  const switchTo = useCallback(
    (tenantId: string | null) => {
      setActiveId(tenantId);
      rememberTenant(userId, tenantId);
    },
    [userId]
  );

  const refresh = useCallback(async () => {
    const mine = await load();
    setActiveId((id) => resolveActiveTenant(mine, id)?.id ?? null);
  }, [load]);

  const respond = useCallback(
    async (invitation: TenantInvitation, accept: boolean) => {
      await respondToInvitation(invitation.id, accept);
      await refresh();
    },
    [refresh]
  );

  const value = useMemo<ActiveTenantValue>(() => {
    const role = active?.role ?? 'ADMINISTRATOR';
    return {
      tenants: tenants ?? [],
      active,
      role,
      canEdit: role !== 'VIEWER',
      scopeKey: tenantScopeKey(userId, activeTenantId),
      invitations,
      roleIn: (tenantId) =>
        tenantId === undefined
          ? role
          : tenantId === null
            ? 'ADMINISTRATOR'
            : ((tenants ?? []).find((tenant) => tenant.id === tenantId)?.role ?? 'VIEWER'),
      nameOf: (tenantId) =>
        tenantId === null ? null : ((tenants ?? []).find((tenant) => tenant.id === tenantId)?.name ?? null),
      switchTo,
      respond,
      refresh
    };
  }, [active, activeTenantId, invitations, refresh, respond, switchTo, tenants, userId]);

  if (remembered && tenants === null) {
    return (
      <main className="auth-callback">
        <p>{t('common.loading')}</p>
      </main>
    );
  }
  return <ActiveTenantContext.Provider value={value}>{children}</ActiveTenantContext.Provider>;
}
